import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, X, Search } from 'lucide-react';

export interface ComboOption { code: string; label: string }

interface Props {
  value: string | null;
  onChange: (code: string | null) => void;
  options: ComboOption[];
  placeholder?: string;
  label?: string;
  testId?: string;
  emptyText?: string;
}

/**
 * Combobox searchable, leggero, senza dipendenze esterne (solo lucide).
 *
 * - Mostra il label dell'opzione selezionata; chip × per pulire.
 * - Apertura su click; ricerca testuale fuzzy (substring case-insensitive).
 * - Chiusura su click-outside o ESC.
 *
 * Pensato per liste anche con centinaia/migliaia di opzioni
 * (es. marche prodotto, categorie merceologiche).
 */
export function Combobox({
  value, onChange, options,
  placeholder = 'Seleziona…',
  label,
  testId,
  emptyText = 'Nessun risultato',
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => options.find(o => o.code === value) || null, [options, value]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      o.label.toLowerCase().includes(q) ||
      o.code.toLowerCase().includes(q),
    );
  }, [options, search]);

  // Click-outside + ESC
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setSearch('');
  }, [open]);

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      {label && (
        <div className="text-[13px] font-semibold text-[#5c5c57] mb-1.5">{label}</div>
      )}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={`w-full flex items-center gap-2 px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] text-[14px] bg-white hover:border-[#b8b8b3] focus:outline-none focus:border-[#2b3a8f] transition-colors ${
          selected ? 'text-[#1f1f1d] font-medium' : 'text-[#5c5c57]'
        }`}
      >
        <span className="truncate flex-1 text-left">
          {selected ? selected.label : placeholder}
        </span>
        {selected && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange(null); } }}
            data-testid={testId ? `${testId}-clear` : undefined}
            className="text-[#5c5c57] hover:text-[#8a3b3b] cursor-pointer flex"
            aria-label="Pulisci"
          >
            <X className="w-3 h-3" />
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-[#5c5c57] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#d8d8d4] rounded-[9px] shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#eee] px-2.5 py-2">
            <Search className="w-3.5 h-3.5 text-[#5c5c57]" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca…"
              data-testid={testId ? `${testId}-search` : undefined}
              className="flex-1 text-[14px] outline-none placeholder:text-[#6d6d68]"
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[14px] text-[#5c5c57] text-center">{emptyText}</div>
            ) : (
              filtered.map(opt => {
                const isSel = opt.code === value;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => { onChange(opt.code); setOpen(false); }}
                    data-testid={testId ? `${testId}-option-${opt.code}` : undefined}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-[14px] text-left hover:bg-[#f3f3f0] transition-colors ${
                      isSel ? 'bg-[#eef1fb] text-[#2b3a8f] font-semibold' : 'text-[#3a3a36]'
                    }`}
                  >
                    <Check className={`w-3 h-3 flex-none ${isSel ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="truncate flex-1">{opt.label}</span>
                    {opt.code !== opt.label && (
                      <span className="text-[12px] font-mono text-[#5c5c57]">{opt.code}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
